import { ReactNode, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

interface UsePrintProps {
  documentTitle?: string
  pageSize?: string;
  fontSize?: string;
  forceColors?: boolean;
  backgroundColor?: string;
  color?: string;
  onAfterPrint?: VoidFunction;
}

interface PrintParams {
  content: ReactNode;
}

const usePrint = ({
  documentTitle,
  onAfterPrint,
  pageSize,
  fontSize,
  forceColors,
  backgroundColor = '#ffffff',
  color = '#1e1e1e',
}: UsePrintProps) => {
  const printContentRef = useRef(null);

  const [printContent, setPrintContent] = useState<PrintParams>()

  const reactToPrintFn = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: documentTitle,
    pageStyle: `
        @media print {
          @page { size: ${pageSize} !important; margin: 0 !important; }

          ${forceColors ? `
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            html, body, body > * {
              background-color: white !important;
              background: white !important;
            }
          ` : ''}

          html {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
          }

          body {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            margin: 0 !important;
            font-size: ${fontSize};
            background-color: ${backgroundColor};
            color: ${color};
          }

          thead {
            display: table-header-group !important;
          }

          tbody {
            display: table-row-group !important;
          }

          /* Evita que uma linha seja cortada no meio entre páginas */
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
        @media print and (max-width: 190mm) {
          body {
            size: 70mm auto;
            font-size: 5px;
          }
        }
      `,
    onAfterPrint() {
      setPrintContent(undefined);

      onAfterPrint?.();
    },
  });

  const print = ({ content }: PrintParams) => {
    setPrintContent({ content })

    setTimeout(() => {
      reactToPrintFn();
    });
  }

  return {
    print,
    contextProps: {
      ref: printContentRef,
      content: printContent?.content || '',
      backgroundColor,
      color,
    },
  }
}

export default usePrint;
